/*
 *   File : listing.js
 *   Author URI : https://evoqins.com
 *   Description : SIP transactions done in the platform
 *   Integrations : react-toastify
 *   Version : v1.1
 */

import { Fragment, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

import { BreadCrumb } from "../BreadCrumb"
import { InvestmentHeader } from "../Header"
import { CustomTab } from "../Tab";
import { Chip, CustomLoader, EmptyScreen } from "../Other";
import Footer from "../Footer/footer";
import { Icon } from "../Icon";
import { SIPTable } from "../Table";
import { SIPCard } from "../Card";
import { ActivateSIPModal } from "../Modal";
import { _consent2FA } from "../../Helper/api";
import { useClickOutside } from "../../Helper";
import { CheckBoxGroup } from "../Form-elements";
import { GradientButton, OutlineButton } from "../CTA";

import APIService from "../../Service/api-service";

import NoData from '../../Assets/Images/nodata/sip.svg';

const PAGE_NAVIGATION = [
    { page: 'Home', url: '/' },
    { page: 'Manage SIPs', url: null },
];

const TAB_DATA = ['Active SIPs', 'Paused SIPs', 'Cancelled SIPs'];

const CHIP_DATA = ['All', 'Mutual funds', 'Life goals'];

/** Maps to sip/active-list POST `include_old` in body; sip/cancelled-list GET with `include_old` query */
const INCLUDE_OLD_FILTER_OPTIONS = [
    { id: 'both', name: 'All' },
    { id: 'new_sip', name: 'New SIPs' },
    { id: 'old_sip', name: 'Old SIPs' },
];


const Listing = () => {

    const navigator = useNavigate();
    const location = useLocation();
    const includeOldMenuRef = useRef(null);
    const [tabIndex, setTabIndex] = useState(1);
    const [chipFilter, setChipFilter] = useState(1);
    const [selectedRow, setSelectedRowId] = useState(null);
    const [pageNavigation, setPageNavigation] = useState(PAGE_NAVIGATION);
    const [apiLoader, setApiLoader] = useState(false);
    const [pausedSIP, setPausedSIP] = useState([]);
    const [cancelledSIP, setCancelledSIP] = useState([]);
    const [includeOldApplied, setIncludeOldApplied] = useState('both');
    const [includeOldDraft, setIncludeOldDraft] = useState(['both']);
    const [showIncludeOldFilter, setShowIncludeOldFilter] = useState(false);
    const [includeOldFilterBadge, setIncludeOldFilterBadge] = useState(0);
    const [selectedSIP, setSelectedSIP] = useState(null);
    const [sipActionLoader, setSIPActionLoader] = useState(false);
    const [loader, setLoader] = useState(true);
    const [initialLoader, setInitialLoader] = useState(true);
    const [sipList, setSipList] = useState({});
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {

        function _handleScroll() {
            setScreenWidth(window.innerWidth);
        }
        window.addEventListener('scroll', _handleScroll);

        return () => {
            window.removeEventListener('scroll', _handleScroll);
        }
    }, []);
    // scroll to top
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useClickOutside(includeOldMenuRef, () => {
        setShowIncludeOldFilter(false);
    });

    useEffect(() => {
        if (location.state !== null) {
            const rawTab = location.state.selectedTab;
            const tab = rawTab === 4 ? 1 : rawTab;
            switch (tab) {
                case 1: _getSipList();
                    break;
                case 2: _getPausedSIP();
                    break;
                case 3: _getCancelledSIP();
                    break;
                default: break;
            }
            setTabIndex(tab);
            if (location.state.reload) {
                setTimeout(() => {
                    _getSipList(true)
                }, 5000);
            }
        }
    }, [location])

    useEffect(() => {
        if (selectedSIP !== null) {
            const modal = new Modal(document.getElementById("active-sip"), {});
            modal.show();
        }
    }, [selectedSIP])

    useEffect(() => {
        _getSipList();
    }, [chipFilter]);

    // handler for tab change
    const _handleTabSelect = (index) => {
        setShowIncludeOldFilter(false);
        setTabIndex(index);
        switch (index) {
            case 1: _getSipList();
                break;
            case 2: _getPausedSIP();
                break;
            case 3: _getCancelledSIP();
                break;
            default: return;
        }
    }

    const _toggleIncludeOldFilter = () => {
        setShowIncludeOldFilter((prev) => {
            const next = !prev;
            if (!prev) {
                setIncludeOldDraft([includeOldApplied]);
            }
            return next;
        });
    };

    const _handleIncludeOldDraft = (id) => {
        setIncludeOldDraft([id]);
    };

    const _applyIncludeOldFilter = () => {
        setShowIncludeOldFilter(false);
        const value = includeOldDraft[0] || 'both';
        setIncludeOldApplied(value);
        setIncludeOldFilterBadge(value === 'both' ? 0 : 1);
        if (tabIndex === 1) {
            _getSipList(false, value);
        } else if (tabIndex === 3) {
            _getCancelledSIP(value);
        }
    };

    const _clearIncludeOldFilter = () => {
        setShowIncludeOldFilter(false);
        setIncludeOldDraft(['both']);
        setIncludeOldApplied('both');
        setIncludeOldFilterBadge(0);
        if (tabIndex === 1) {
            _getSipList(false, 'both');
        } else if (tabIndex === 3) {
            _getCancelledSIP('both');
        }
    };

    // handler for chip filter
    function _handleChipFilter(selected_status) {
        if (selected_status != chipFilter) {
            setLoader(true);
            setChipFilter(selected_status);
        }
    };

    // API : Get paused SIPs
    function _getPausedSIP() {
        setApiLoader(true);
        let url = 'sip/paused-list';
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                setPausedSIP(response.data);
            }
            else {
                setPausedSIP([]);
            }
            setApiLoader(false);
        })
    }

    // API : Get cancelled SIP list
    function _getCancelledSIP(includeOldParam) {
        setApiLoader(true);
        const v = includeOldParam !== undefined ? includeOldParam : includeOldApplied;
        const q = encodeURIComponent(v);
        let url = `sip/cancelled-list?include_old=${q}`;
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                setCancelledSIP(response.data);
            } else {
                setCancelledSIP([]);
            }
            setApiLoader(false);
        })
    }

    // API : Resume a paused SIP
    async function _resumeSIP(pin) {
        setSIPActionLoader(true);
        let status = true;
        let url = 'sip/resume';
        let payload = JSON.stringify({
            sip_id: selectedSIP,
            otp: pin
        });
        await APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                toast.success('SIP resumed successfully', {
                    type: 'success'
                });
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error"
                });
                status = false;
            }
            setSIPActionLoader(false);
        });
        return status;
    }

    async function _activateSIP() {

    }

    function _getSIP() {

    }

    // API - Sip listing 
    // function _getSipList() {
    //     setChipFilter(1);
    //     let url = 'sip/active-list';
    //     let payload = JSON.stringify({
    //         is_goal: chipFilter === 1 ? null : chipFilter === 2 ? false : true
    //     });
    //     APIService(true, url, payload).then((response) => {
    //         if (response.status_code === 200) {
    //             setSipList(response.data)
    //             setLoader(false);
    //             setInitialLoader(false);
    //         } else {
    //             setLoader(false);
    //             setInitialLoader(false);
    //             setSipList([]);
    //         }
    //     });
    // }


    async function _sendOTP(id, type) {
        _consent2FA(type)
            .then((response) => {
                toast.dismiss();
                toast.success("OTP sent successfully", {
                    type: "success",
                });
                setSelectedSIP(id);
            }).catch((response) => {
                toast.dismiss();
                toast.error(response, {
                    type: "error"
                });
            });
    }

    const HeadingCard = (props) => {
        return (
            <div className="border-radius-10px border-all border-primary-color padding-22px-lr py-2 text-center">
                <img src={props.image}
                    alt={props.title}
                    width={24}
                    height={24} />
                <p className="color-gray e-poppins-medium e-font-14 line-height-sm-14px-20px mb-2 mt-1">{props.title}</p>
                <p className="color-black e-poppins-semi-bold e-font-14 line-height-16px mb-0">
                    <span className="e-inter-semi-bold">{props.rupee === true && "₹"}
                    </span>
                    {props.value}
                </p>
            </div>

        )
    }

    const _handleSipDetail = (data) => {
        setSelectedRowId(data);
        // setPageNavigation([...pageNavigation, "SIP detail"])
    }

    // API - sip lisiting
    function _getSipList(reload = false, includeOldParam) {
        let url = 'sip/active-list';
        const includeOldVal = includeOldParam !== undefined ? includeOldParam : includeOldApplied;
        let payload = JSON.stringify({
            is_goal: chipFilter === 1 ? null : chipFilter === 2 ? false : true,
            include_old: includeOldVal,
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setSipList(response.data)
                setLoader(false);
                setInitialLoader(false);
                if (reload) {
                    toast.success('List automatically reloaded for fetching latest SIPs');
                    navigator({ state: { reload: false } })
                }
            }
        });
    }

    const includeOldFilterControl = (
        <div className="position-relative" ref={includeOldMenuRef}>
            <div className={`d-flex align-items-center border-radius-6px border-all p-2 cursor-pointer ${showIncludeOldFilter === true ? 'border-primary-color' : 'border-bright-gray'}`}
                onClick={_toggleIncludeOldFilter}>
                <p className="color-charleston-green e-poppins-medium e-font-14 line-height-24px mb-0 me-1">
                    Filter
                </p>
                <img src={require('../../Assets/Images/explore/filter.png')}
                    alt="Filter"
                    width={24}
                    height={24} />
                {
                    includeOldFilterBadge !== 0 &&
                    <p className="color-white border-radius-100px e-poppins-bold e-font-12 line-height-12px e-badge position-absolute mb-0 bg-sea-green d-flex justify-content-center align-items-center">
                        {includeOldFilterBadge}
                    </p>
                }
            </div>
            {
                showIncludeOldFilter === true ?
                    <Fragment>
                        <div className="position-absolute right-0 d-flex flex-column gap-12px border-radius-12px border-solid border-width-2px border-gainsboro e-order-filter z-index-10 bg-cards"
                            >
                            {
                                INCLUDE_OLD_FILTER_OPTIONS.map((item, key) =>
                                    <CheckBoxGroup selectedType={includeOldDraft}
                                        item={item}
                                        spanClassName="e-poppins-medium"
                                        key={key}
                                        onHandleClick={_handleIncludeOldDraft} />
                                )
                            }
                            <div className="d-flex gap-8px justify-content-end">
                                <OutlineButton label="Clear"
                                    className="py-2 px-3 e-poppins-regular e-font-14 line-height-14px"
                                    handleClick={_clearIncludeOldFilter} />
                                <GradientButton label="Filter"
                                    className="py-2 px-3 e-poppins-regular e-font-14 line-height-14px"
                                    handleClick={_applyIncludeOldFilter} />
                            </div>
                        </div>
                        <div className="position-fixed w-100 z-index-8 h-100 left-0 top-0 cursor-default"
                            onClick={() => setShowIncludeOldFilter(false)}></div>
                    </Fragment>
                    :
                    null
            }
        </div>
    );

    return (

        <Fragment>
            <div className="e-page-container e-manage-sip e-order margin-80px-bottom">

                {/* Header */}
                <InvestmentHeader />
                {
                    initialLoader === true ?
                        <div className="h-80vh">
                            <CustomLoader />
                        </div>
                        :
                        <>
                            <BreadCrumb data={pageNavigation} />

                            {/* NO data image preload */}
                            <img src={NoData} className="d-none" />
                            {
                                selectedRow !== null ?
                                    <p className="color-black e-poppins-medium e-font-20 line-height-32px mt-sm-4 mt-2 mb-sm-4 mb-0">My SIP</p>
                                    :
                                    <p className="color-black e-poppins-medium e-font-20 line-height-32px mt-sm-4 mt-2 mb-sm-4 mb-0">
                                        {
                                            tabIndex === 1 ?
                                                `Active SIPs(${sipList.fund_data?.length ?? 0})`
                                                : tabIndex === 2 ?
                                                    `Paused SIPs(${pausedSIP.length})`
                                                    : `Cancelled SIPs(${cancelledSIP.length})`
                                        }
                                    </p>
                            }

                            {/* Tab select */}
                            <CustomTab data={TAB_DATA}
                                selectedTab={tabIndex}
                                indexToHide={1}
                                wrapperClass={'gap-16px'}
                                className="px-1 padding-10px-tb e-order-tab"
                                onSelectTab={_handleTabSelect} />
                            <>
                                {
                                    apiLoader === true ? (
                                        <div className="row">
                                            <div className="col-12 text-center pt-5 mt-5">
                                                <CustomLoader />
                                            </div>
                                        </div>
                                    ) :
                                        tabIndex === 1 ?
                                            <Fragment>
                                                <div className="col-lg-8 mt-4">
                                                    <div className=" border-radius-8px bg-antique-white py-2 padding-6px-lr mb-3">
                                                        <div className="row">
                                                            <div className="col-1 pe-0 text-center">
                                                                <Icon icon="info"
                                                                    size={24} />
                                                            </div>
                                                            <div className="col-11">
                                                                <p className="color-jett-black e-poppins-regular e-font-14 mb-0">
                                                                    The SIPs displayed here are based on the orders placed via MT Wealth. Reports for older SIPs will be provided separately. Your Portfolio and related reports reflect all your transactions, whether executed on the old platform or through MT Wealth.

                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mt-4 margin-32px-bottom d-flex  gap-16px">
                                                    <HeadingCard image={require("../../Assets/Images/Sidebar/manage-sips-color.png")}
                                                        title="Active SIPs"
                                                        value={sipList.total_active_sip ?? '-'}
                                                        rupee={false} />
                                                    <HeadingCard image={require("../../Assets/Images/manage-sip/total-amount.png")}
                                                        title="Total amount"
                                                        value={(sipList.amount_active_sip ?? 0).toLocaleString('En-IN')}
                                                        rupee={true} />
                                                    <HeadingCard image={require("../../Assets/Images/manage-sip/amc-invested.png")}
                                                        title="AMC invested"
                                                        value={sipList.amc_counts ?? '-'}
                                                        rupee={false} />
                                                </div>

                                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-16px mb-4"
                                                    data-disabled={`${loader}`}>
                                                    <div className="d-flex gap-8px flex-wrap">
                                                        {
                                                            CHIP_DATA.map((tag, index) => {
                                                                return (
                                                                    <Chip data={tag}
                                                                        key={index}
                                                                        type={2}
                                                                        chipIndex={index + 1}
                                                                        activeChipIndex={chipFilter}
                                                                        onPress={_handleChipFilter} />
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                    {includeOldFilterControl}
                                                </div>

                                                {
                                                    loader === true ?
                                                        <CustomLoader />
                                                        :
                                                        <div className="row">
                                                            {
                                                                sipList.fund_data && sipList.fund_data.length !== 0 ? (
                                                                    <div className="col-12">
                                                                        {
                                                                            screenWidth > 576 &&
                                                                            <div className="col-12 text-end mt-2 e-table-scroll-indicator-sip">
                                                                                <p className="color-outer-space e-poppins-regular e-font-12 mb-0">
                                                                                    <img draggable={false} src={require('../../../src/Assets/Images/explore/scroll-gif.gif')} alt="scroll"
                                                                                        width='24px'
                                                                                        height='24px'
                                                                                        className='e-rotate-gif' />
                                                                                    Scroll horizontally for view more  information</p>
                                                                            </div>
                                                                        }
                                                                        <SIPTable data={sipList.fund_data}
                                                                            showSipDetail={_handleSipDetail} />
                                                                    </div>
                                                                ) : (
                                                                    <div className="col-12 text-center mt-5">
                                                                        <EmptyScreen image={NoData}
                                                                            title="Not yet initiated a SIP?"
                                                                            description=" Using SIPs to invest is a wise strategy to reduce market volatility and creating wealth in long term."
                                                                            buttonLabel="Explore fund"
                                                                            onPress={() => navigator('/explore')} />
                                                                    </div>
                                                                )
                                                            }

                                                        </div>

                                                }
                                            </Fragment>

                                            :
                                            tabIndex === 2 ?
                                                <div className="row margin-32px-top">
                                                    {
                                                        pausedSIP.length !== 0 ? (
                                                            <div className="col-xl-9">
                                                                <div className="d-flex flex-column gap-16px">
                                                                    {
                                                                        pausedSIP.map((item, key) =>
                                                                            <SIPCard type={1}
                                                                                data={item}
                                                                                key={key}
                                                                                resumeSIP={_sendOTP} />
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="col-12 text-center mt-5">
                                                                <EmptyScreen image={NoData}
                                                                    title="There are no SIPs currently on hold"
                                                                    description=" "
                                                                    buttonLabel="Explore fund"
                                                                    onPress={() => navigator('/explore')} />
                                                            </div>
                                                        )
                                                    }

                                                </div>
                                                :
                                                tabIndex === 3 ?
                                                <div className="row margin-32px-top">
                                                    <div className="d-flex justify-content-end w-100 mb-3">
                                                        {includeOldFilterControl}
                                                    </div>
                                                    {
                                                        cancelledSIP.length !== 0 ? (
                                                            <div className="col-xl-9 ">
                                                                <div className="d-flex flex-column gap-16px">
                                                                    {
                                                                        cancelledSIP.map((item, key) =>
                                                                            <SIPCard type={2}
                                                                                data={item}
                                                                                key={key} />
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="col-12 text-center mt-5">
                                                                <EmptyScreen image={NoData}
                                                                    title="There are no SIPs currently on cancelled"
                                                                    description=" "
                                                                    buttonLabel="Explore fund"
                                                                    onPress={() => navigator('/explore')} />
                                                            </div>
                                                        )
                                                    }

                                                </div>
                                                : null
                                }
                            </>
                        </>
                }

            </div >
            <Footer />
            {
                selectedSIP !== null && (
                    <ActivateSIPModal type={tabIndex}
                        loader={sipActionLoader}
                        handleOTPSubmission={tabIndex === 1 ? _activateSIP : _resumeSIP}
                        updateList={tabIndex === 1 ? _getSipList : _getPausedSIP}
                        close={() => setSelectedSIP(null)} />
                )
            }

        </Fragment >
    )
}

export default Listing